import type { BoardBinding, BoardMetricKey, Currency, PeriodInput, SheetRow, Unit } from 'chery-contracts';
import { BLANK_DISPLAY, formatValue, isNumericUnit, pillarRank, type Pillar } from 'chery-kpi-engine';
import { type CustomRange, periodFor, type PeriodChoice } from '../reports/reports-display';
import { STAGES, type StageCopy } from './content/divisions';
import type { PresenceAge, PresenceFilter } from './content/presence';

/**
 * Presentation rules of the Board (plan §7) — pure, so they are unit-tested. Every number arrives resolved from
 * `board.direction` / `board.stageSheets`; nothing here recomputes a value, a target or a completion. A blank is `—`,
 * never 0.
 */

// ---------------------------------------------------------------------------------------------------------------
// Period

export const BOARD_PERIOD_CHOICES = ['YTD', 'QUARTER', 'LAST_13', 'LAST_26', 'ALL', 'CUSTOM'] as const;
export type BoardPeriodChoice = (typeof BOARD_PERIOD_CHOICES)[number];

/** Board sheets open on the year to date. */
export const DEFAULT_BOARD_PERIOD: BoardPeriodChoice = 'YTD';

/** The shared `period` shape (the one Reports sends); `null` for Custom until a valid From/To exists. */
export const boardPeriodFor = (choice: BoardPeriodChoice, custom: CustomRange | null): PeriodInput | null =>
  choice === 'YTD' ? { kind: 'YTD' } : periodFor(choice as PeriodChoice, custom);

// ---------------------------------------------------------------------------------------------------------------
// Values

/** `1,450.0`, `JOD 1,450.0`, `12.5%`, or `—` for a blank. */
export const metricText = (value: number | null | undefined, unit: Unit, currency: Currency | null): string =>
  value === null || value === undefined || !isNumericUnit(unit) ? BLANK_DISPLAY : formatValue(unit, value, currency);

/** `54.9%`, or `—`. */
export const percentText = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? BLANK_DISPLAY : formatValue('PERCENTAGE', value);

const COMPACT_STEPS: readonly [number, string][] = [
  [1_000_000_000, 'B'],
  [1_000_000, 'M'],
  [1_000, 'K'],
];

/** `10.6M`, `568`, `1.5K` — the board's way of writing gates. */
export const compactNumber = (value: number): string => {
  const abs = Math.abs(value);

  for (const [step, suffix] of COMPACT_STEPS) {
    if (abs >= step) {
      return `${Number((value / step).toFixed(1)).toLocaleString('en-US')}${suffix}`;
    }
  }

  return Number(value.toFixed(1)).toLocaleString('en-US');
};

/** Bar fill in percent of the track, clamped to 0…100; a blank fills nothing. */
export const fillPercent = (value: number | null | undefined, of: number): number => {
  if (value === null || value === undefined || !Number.isFinite(value) || of <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (value / of) * 100));
};

/** The bars-vs-target scale: completion is drawn against a track running to 150 % (the target marker sits at ⅔). */
export const TARGET_TRACK_PCT = 150;

/** Position of the target marker on that track, in percent of its width. */
export const TARGET_MARKER_AT = (100 / TARGET_TRACK_PCT) * 100;

/** Funnel bar widths: each stage relative to the largest stage; blanks are 0 wide (and printed `—`). */
export const funnelWidths = (rows: readonly Pick<SheetRow, 'value'>[]): number[] => {
  const max = Math.max(0, ...rows.map((row) => row.value ?? 0));

  return rows.map((row) => fillPercent(row.value, max));
};

export interface StackSegment {
  key: string;
  value: number;
  /** Bottom and top of the segment in data units. */
  from: number;
  to: number;
}

export interface StackColumn {
  weekId: string;
  weekDate: string;
  segments: StackSegment[];
  /** `null` when every series is blank that week — a gap, not a zero column. */
  total: number | null;
}

/** Stacked weekly columns from the per-row series; a blank segment is skipped, a week with none is a gap. */
export const stackColumns = (rows: readonly Pick<SheetRow, 'key' | 'series'>[]): { columns: StackColumn[]; max: number } => {
  const first = rows.find((row) => row.series)?.series ?? [];
  const columns = first.map((point, index): StackColumn => {
    let top = 0;
    const segments: StackSegment[] = [];

    for (const row of rows) {
      const value = row.series?.[index]?.value ?? null;

      if (value !== null && value > 0) {
        segments.push({ key: row.key, value, from: top, to: top + value });
        top += value;
      }
    }

    return { weekId: point.weekId, weekDate: point.weekDate, segments, total: segments.length === 0 ? null : top };
  });

  return { columns, max: Math.max(0, ...columns.map((column) => column.total ?? 0)) };
};

// ---------------------------------------------------------------------------------------------------------------
// Stages, bindings

/** The stage of `?stage=` (slug), else the first. */
export const stageFromSlug = (slug: string | null): StageCopy => STAGES.find((stage) => stage.slug === slug) ?? (STAGES[0] as StageCopy);

/** The KPIs a key may be bound to: matching unit and, for Currency, the expected currency; pillar order, then name. */
export const compatibleKpis = <TKpi extends { name: string; pillar: Pillar; unit: Unit; currency: Currency | null }>(
  binding: Pick<BoardBinding, 'expectedUnit' | 'expectedCurrency'>,
  kpis: readonly TKpi[],
): TKpi[] =>
  kpis
    .filter((kpi) => kpi.unit === binding.expectedUnit && (binding.expectedUnit !== 'CURRENCY' || kpi.currency === binding.expectedCurrency))
    .sort((a, b) => pillarRank(a.pillar) - pillarRank(b.pillar) || a.name.localeCompare(b.name));

/** `Currency (JOD)`, `Percentage`. */
export const expectedUnitText = (unit: Unit, currency: Currency | null, unitLabel: (unit: Unit) => string): string =>
  unit === 'CURRENCY' && currency ? `${unitLabel(unit)} (${currency})` : unitLabel(unit);

/** `VOLUME_DELIVERED, VEHICLE_REVENUE` — the keys an unbound hint names. */
export const keyList = (keys: readonly BoardMetricKey[]): string => keys.join(', ');

// ---------------------------------------------------------------------------------------------------------------
// Presence map

export interface Viewport {
  width: number;
  height: number;
  pad: number;
}

/** Mercator northing in the same unit as longitude (degrees), so x and y share one scale. */
const mercatorY = (lat: number): number => (Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) * 180) / Math.PI;

/**
 * A Mercator projection fitted to `outline` inside the viewport (keeping its aspect ratio, centred): `(lon, lat) →
 * [x, y]` in SVG units, y down.
 */
export const fitMercator = (
  outline: readonly (readonly [number, number])[],
  viewport: Viewport,
): ((lon: number, lat: number) => [number, number]) => {
  const xs = outline.map(([lon]) => lon);
  const ys = outline.map(([, lat]) => mercatorY(lat));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const innerW = viewport.width - viewport.pad * 2;
  const innerH = viewport.height - viewport.pad * 2;
  const scale = Math.min(innerW / (maxX - minX), innerH / (maxY - minY));
  const offsetX = viewport.pad + (innerW - (maxX - minX) * scale) / 2;
  const offsetY = viewport.pad + (innerH - (maxY - minY) * scale) / 2;

  return (lon, lat) => [offsetX + (lon - minX) * scale, offsetY + (maxY - mercatorY(lat)) * scale];
};

/** `M x,y L x,y … Z`, one decimal. */
export const outlinePath = (outline: readonly (readonly [number, number])[], project: (lon: number, lat: number) => [number, number]): string =>
  `${outline
    .map(([lon, lat], index) => {
      const [x, y] = project(lon, lat);

      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ')} Z`;

/** A site is highlighted under "All ages" or its own age; the others are dimmed, never hidden. */
export const siteHighlighted = (age: PresenceAge, filter: PresenceFilter): boolean => filter === 'ALL' || filter === age;
